import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    if (!localStorage.getItem("token")) return null;
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { localStorage.removeItem("user"); return null; }
  });
  const [loading, setLoading] = useState(true);

  const verify = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/api/auth/me");
      if (localStorage.getItem("token") !== token) return;
      setUser(data);
      localStorage.setItem("user", JSON.stringify(data));
    } catch (err) {
      // A temporary server/network error must not erase a valid login.
      if (err.response?.status === 401 && localStorage.getItem("token") === token) {
        setUser(null);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { verify(); }, [verify]);

  useEffect(() => {
    const expire = () => setUser(null);
    const onStorage = (event) => {
      if (event.key === "token" || event.key === null) verify();
    };
    window.addEventListener("auth:expired", expire);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("auth:expired", expire);
      window.removeEventListener("storage", onStorage);
    };
  }, [verify]);

  const login = async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const signup = async (formData) => {
    const { data } = await api.post("/api/auth/signup", formData);
    localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    const token = localStorage.getItem("token");
    const { data } = await api.put("/api/auth/profile", profileData);
    if (localStorage.getItem("token") !== token) return;
    const updated = data.user || { ...user, ...profileData };
    setUser(updated);
    localStorage.setItem("user", JSON.stringify(updated));
    return updated;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
